import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCasinoWallet, spinSlots } from "../lib/api";

const SYMBOLS = {
  toilet: "🚽",
  plunger: "🪠",
  paper: "🧻",
  poop: "💩",
  diamond: "💎",
};

const ALL_SYMBOLS = Object.keys(SYMBOLS);
const BET_OPTIONS = [1, 5, 10, 25, 50, 100];

export default function Slots() {
  const [wallet, setWallet] = useState(null);
  const [bet, setBet] = useState(5);
  const [reels, setReels] = useState(["toilet", "paper", "poop"]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [spinReels, setSpinReels] = useState([[], [], []]);
  const navigate = useNavigate();
  const intervalRefs = useRef([]);

  useEffect(() => {
    getCasinoWallet().then(setWallet).catch(() => {});
  }, []);

  const spin = useCallback(async () => {
    if (spinning) return;
    if (!wallet || wallet.tokens < 1) return;
    if (wallet.coins < bet) return;

    setSpinning(true);
    setResult(null);

    // Start visual spin animation
    const tempReels = [[], [], []];
    for (let i = 0; i < 3; i++) {
      const arr = [];
      for (let j = 0; j < 20; j++) arr.push(ALL_SYMBOLS[Math.floor(Math.random() * ALL_SYMBOLS.length)]);
      tempReels[i] = arr;
    }
    setSpinReels(tempReels);

    try {
      const res = await spinSlots(bet);
      // Stop reels one by one with delay
      setTimeout(() => {
        setReels(prev => [res.reels[0], prev[1], prev[2]]);
      }, 400);
      setTimeout(() => {
        setReels(prev => [prev[0], res.reels[1], prev[2]]);
      }, 700);
      setTimeout(() => {
        setReels([res.reels[0], res.reels[1], res.reels[2]]);
        setSpinning(false);
        setResult(res);
        setWallet(w => ({ ...w, coins: res.coins, tokens: res.tokens }));
      }, 1000);
    } catch (e) {
      setSpinning(false);
      setResult({ error: e.message });
    }
  }, [spinning, wallet, bet]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => intervalRefs.current.forEach(clearInterval);
  }, []);

  const displaySymbol = (idx) => {
    if (spinning && spinReels[idx]?.length > 0) return null;
    return SYMBOLS[reels[idx]] || "?";
  };

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <style>{`
        @keyframes slotSpin {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100%); }
        }
        .reel-spin {
          animation: slotSpin 0.15s linear infinite;
        }
      `}</style>

      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <button className="text-sm text-neutral-500" onClick={() => navigate("/casino")}>← casino</button>
        <h1 className="text-2xl font-mono">slots</h1>
        <div className="text-right">
          <div className="text-[10px] text-neutral-400">tokens</div>
          <div className="text-sm font-mono">{wallet?.tokens ?? 0}</div>
        </div>
      </div>

      {wallet && (
        <div className="px-6 pb-2 text-center">
          <span className="font-mono text-lg">{wallet.coins} coins</span>
        </div>
      )}

      {/* Slot Machine */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="border-2 border-neutral-300 rounded-2xl p-4 bg-neutral-50 shadow-inner">
          <div className="flex gap-3">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="w-20 h-24 border border-neutral-200 rounded-xl bg-white flex items-center justify-center overflow-hidden"
              >
                {spinning ? (
                  <div className="reel-spin flex flex-col items-center">
                    {(spinReels[i] || []).map((s, j) => (
                      <span key={j} className="text-4xl leading-[96px]">{SYMBOLS[s]}</span>
                    ))}
                  </div>
                ) : (
                  <span className="text-4xl">{SYMBOLS[reels[i]] || "?"}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Result */}
        {result && !result.error && (
          <div className={`mt-4 text-center ${result.payout > 0 ? "text-green-600" : "text-neutral-400"}`}>
            {result.payout > 0 ? (
              <div>
                <div className="text-xl font-mono font-bold">+{result.payout} coins!</div>
                <div className="text-sm">{result.multiplier}x multiplier</div>
              </div>
            ) : (
              <div className="text-sm">No luck this time</div>
            )}
          </div>
        )}

        {result?.error && (
          <div className="mt-4 text-sm text-red-500 text-center">{result.error}</div>
        )}

        {/* Bet Selector */}
        <div className="mt-6 w-full max-w-xs">
          <div className="text-[11px] text-neutral-500 text-center mb-2">bet amount</div>
          <div className="flex gap-2 justify-center flex-wrap">
            {BET_OPTIONS.map(b => (
              <button
                key={b}
                className={`rounded-full px-3 py-1.5 text-sm font-mono border transition-colors ${
                  bet === b
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-neutral-200"
                }`}
                onClick={() => setBet(b)}
                disabled={spinning}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {/* Spin Button */}
        <button
          className={`mt-6 rounded-full px-10 py-4 text-lg font-medium transition-colors ${
            spinning || !wallet || wallet.tokens < 1 || wallet.coins < bet
              ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
              : "bg-black text-white"
          }`}
          disabled={spinning || !wallet || wallet.tokens < 1 || wallet.coins < bet}
          onClick={spin}
        >
          {spinning ? "Spinning..." : "Spin (1 token)"}
        </button>

        {wallet && wallet.tokens < 1 && (
          <div className="mt-3 text-xs text-neutral-400 text-center">No tokens — do an activity to earn one!</div>
        )}
      </div>

      {/* Payout Table */}
      <div className="px-6 pb-6">
        <div className="text-[10px] text-neutral-400 text-center mb-1">payouts</div>
        <div className="flex justify-center gap-3 text-[10px] text-neutral-500 font-mono">
          <span>3x💎=100x</span>
          <span>3x💩=20x</span>
          <span>3x🧻=10x</span>
          <span>2x=2x</span>
        </div>
      </div>
    </div>
  );
}
